import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent } from '../../components/ui';
import { Button, Input, PaginatedTable, Field, FieldLabel } from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import toast from 'react-hot-toast';

type Mode = 'broadcast' | 'targeted';

interface NotificationHistoryEntry {
  id: string;
  kind: 'broadcast' | 'targeted';
  title: string;
  body: string | null;
  targetsSummary: string | null;
  sentAt: string;
  sentByUserId: string;
}

export default function NotificationsPage() {
  const [mode, setMode] = useState<Mode>('broadcast');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [academyIds, setAcademyIds] = useState('');
  const [roles, setRoles] = useState('');
  const [loading, setLoading] = useState(false);

  const [historyEntries, setHistoryEntries] = useState<NotificationHistoryEntry[]>([]);
  const [historyPagination, setHistoryPagination] = useState<PaginationState | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    adminBackofficeApi
      .getNotificationHistory({ page: historyPage, limit: 20 })
      .then((data) => {
        if (data.success && data.data) {
          setHistoryEntries(data.data.entries ?? []);
          setHistoryPagination(data.data.pagination ?? null);
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [historyPage]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'broadcast') {
        const res = await adminBackofficeApi.sendBroadcastNotification({ title: title.trim(), body: body.trim() });
        if (res.success) {
          toast.success('Broadcast sent');
          loadHistory();
        } else toast.error(res.error || 'Failed');
      } else {
        const targets: { userIds?: string[]; academyIds?: string[]; roles?: string[] } = {};
        if (academyIds.trim()) targets.academyIds = academyIds.split(/[\s,]+/).filter(Boolean);
        if (roles.trim()) targets.roles = roles.split(/[\s,]+/).filter(Boolean) as string[];
        if (!targets.academyIds?.length && !targets.roles?.length) {
          toast.error('Specify at least one academy ID or role for targeted notification');
          setLoading(false);
          return;
        }
        const res = await adminBackofficeApi.sendTargetedNotification({
          title: title.trim(),
          body: body.trim(),
          targets,
        });
        if (res.success) {
          toast.success(`Sent to ${res.data?.sentTo ?? 'targets'}`);
          loadHistory();
        } else toast.error(res.error || 'Failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to send');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Send notifications"
        help={<p>Broadcast or send targeted in-app notifications to users.</p>}
      />
      <Card>
        <CardHeader>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('broadcast')}
              className={`px-3 py-1 rounded text-sm ${mode === 'broadcast' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-gray-100 dark:bg-gray-800'}`}
            >
              Broadcast
            </button>
            <button
              type="button"
              onClick={() => setMode('targeted')}
              className={`px-3 py-1 rounded text-sm ${mode === 'targeted' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-gray-100 dark:bg-gray-800'}`}
            >
              Targeted
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field>
            <FieldLabel htmlFor="notification-title">Title</FieldLabel>
            <Input id="notification-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Notification title" />
          </Field>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Message body"
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              rows={3}
            />
          </div>
          {mode === 'targeted' && (
            <>
              <Field>
                <FieldLabel htmlFor="notification-academy-ids">Academy IDs (comma or space separated)</FieldLabel>
                <Input id="notification-academy-ids" value={academyIds} onChange={(e) => setAcademyIds(e.target.value)} placeholder="academy-id-1, academy-id-2" />
              </Field>
              <Field>
                <FieldLabel htmlFor="notification-roles">Roles (e.g. academy_owner, student)</FieldLabel>
                <Input id="notification-roles" value={roles} onChange={(e) => setRoles(e.target.value)} placeholder="academy_owner, student" />
              </Field>
            </>
          )}
          <Button onClick={handleSend} disabled={loading}>
            {loading ? 'Sending…' : 'Send'}
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Notification history</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Broadcast and targeted notifications sent from this backoffice
          </p>
        </CardHeader>
        <CardContent>
          <PaginatedTable<NotificationHistoryEntry>
            columns={[
              { key: 'kind', header: 'Type', render: (e) => e.kind },
              { key: 'title', header: 'Title', render: (e) => e.title },
              {
                key: 'body',
                header: 'Body',
                render: (e) => (e.body ? (e.body.length > 80 ? e.body.slice(0, 80) + '…' : e.body) : '–'),
              },
              { key: 'targets', header: 'Targets', render: (e) => e.targetsSummary ?? '–' },
              {
                key: 'sentAt',
                header: 'Sent',
                render: (e) => (e.sentAt ? new Date(e.sentAt).toLocaleString() : '–'),
              },
            ]}
            data={historyEntries}
            pagination={historyPagination}
            loading={historyLoading}
            emptyMessage="No notification history yet."
            onPageChange={setHistoryPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
